import type { CapQuotaStatus, QuotaCap, TenantQuotaStatus } from "../api/types";

export interface QuotaNotice {
  level: "warning" | "hard_stop";
  title: string;
  body: string;
  /** Admin-only figure lines, one per binding cap; empty for a member. */
  figures: string[];
}

const CAP_LABEL: Record<QuotaCap, string> = {
  daily_burst: "daily burst",
  monthly: "monthly",
};
const CAPS: QuotaCap[] = ["daily_burst", "monthly"];

function capsAt(
  status: TenantQuotaStatus,
  level: "warning" | "hard_stop",
): QuotaCap[] {
  return CAPS.filter((cap) => status[cap].level === level);
}
const joinCaps = (caps: QuotaCap[]) =>
  caps.map((c) => CAP_LABEL[c]).join(" and ");
const bindingCap = (
  status: TenantQuotaStatus,
  caps: QuotaCap[],
): CapQuotaStatus =>
  caps
    .map((c) => status[c])
    .sort((a, b) => b.window_end.localeCompare(a.window_end))[0];
const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
const figureLine = (cap: QuotaCap, c: CapQuotaStatus): string | null => {
  if (!c.usage) return null;
  const label = CAP_LABEL[cap][0].toUpperCase() + CAP_LABEL[cap].slice(1);
  const used = c.usage.used_tokens.toLocaleString("en-US");
  return c.usage.cap_tokens === null
    ? `${label}: ${used} tokens, no cap`
    : `${label}: ${used} of ${c.usage.cap_tokens.toLocaleString("en-US")} tokens`;
};

export function describeQuotaStatus(
  status: TenantQuotaStatus,
): QuotaNotice | null {
  if (status.level === "normal") return null;
  const level = status.level;
  const caps = capsAt(status, level);
  if (caps.length === 0) return null;
  const figures = caps
    .map((c) => figureLine(c, status[c]))
    .filter((l): l is string => l !== null);
  if (level === "hard_stop") {
    return {
      level,
      figures,
      title: "AI features are paused",
      body: `This organization has reached its ${joinCaps(caps)} AI token budget. Your work is saved; AI resumes ${formatWhen(bindingCap(status, caps).window_end)}.`,
    };
  }
  return {
    level,
    figures,
    title: "AI usage is near this organization's limit",
    body: `More than ${status.thresholds.warning_pct}% of the ${joinCaps(caps)} AI token budget is used. AI features keep working until ${status.thresholds.hard_stop_pct}%; your work is never blocked.`,
  };
}

export function quotaDismissKey(status: TenantQuotaStatus): string | null {
  if (status.level === "normal") return null;
  const caps = capsAt(status, status.level);
  if (caps.length === 0) return null;
  return `${status.tenant_id}:${status.level}:${bindingCap(status, caps).window_start}`;
}
